import React from "react";

interface FeatureDetailsProps {
    featureId: string;
    title: string;
    description: string;
    product: string;
    source?: string;
    owner: string;
    createdDate: string;
    url?: string;
}

// Helper: convert only URLs in description to clickable links; keep all other text plain
const renderDescription = (text: string) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s)]+)(?![^<]*?>)/g;
    // Preserve original line breaks by replacing them with spaces to avoid large vertical space
    const normalized = text.replace(/\r?\n+/g, " ").trim();
    const segments = normalized.split(urlRegex);
    return segments.map((seg, idx) => {
        if (seg.match(urlRegex)) {
            return (
                <a
                    key={idx}
                    href={/^https?:\/\//.test(seg) ? seg : "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: "#2563eb",
                        fontWeight: 600,
                        textDecoration: "none",
                        borderBottom: "1px solid rgba(37,99,235,0.35)"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderBottom = "1px solid rgba(37,99,235,0.7)"}
                    onMouseLeave={(e) => e.currentTarget.style.borderBottom = "1px solid rgba(37,99,235,0.35)"}
                >{seg}</a>
            );
        }
        return <span key={idx}>{seg}</span>;
    });
};

const FeatureDetailsCard: React.FC<FeatureDetailsProps> = ({
    featureId,
    title,
    description,
    product,
    source,
    owner,
    createdDate,
    url
}) => {
    return (
        <div
            style={{
                width: "96%",
                maxWidth: "96%",
                margin: "0 auto 8px auto",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                borderRadius: 14,
                padding: "20px 28px 24px 28px",
                boxShadow: "0 6px 24px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.03)",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                position: "relative",
                overflow: "hidden",
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
        >
            {/* Decorative gradient overlay */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "linear-gradient(90deg, #3b82f6 100%)",
                }}
            />

            {/* Top Section: Header + Badge */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 18,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: 17,
                        fontWeight: 700,
                        color: "#0f172a",
                        gap: 10,
                        letterSpacing: "-0.01em",
                    }}
                >
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: "10px",
                            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "#fff",
                            fontSize: 16,
                            fontWeight: 700,
                            boxShadow: "0 3px 10px rgba(59, 130, 246, 0.25)",
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                    </div>

                    Feature Request Details
                </div>

                {/* Right side badge */}
                <a
                    href={url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        background: "#E5EDFF",
                        color: "#2563EB",
                        padding: "8px 16px",
                        borderRadius: 20,
                        fontSize: 15,
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        display: "inline-block"
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#2563EB";
                        e.currentTarget.style.color = "#ffffff";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#E5EDFF";
                        e.currentTarget.style.color = "#2563EB";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                    }}
                >
                    {featureId}
                </a>
            </div>

            {/* Two column layout */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 280px",
                    columnGap: 48,
                }}
            >
                {/* LEFT COLUMN */}
                <div>
                    {/* Title label */}
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#64748b",
                            marginBottom: 7,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                        }}
                    >
                        Title
                    </div>
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#0f172a",
                            marginBottom: 18,
                            lineHeight: "1.3",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {title}
                    </div>

                    {/* Description */}
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#64748b",
                            marginBottom: 7,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                        }}
                    >
                        Description
                    </div>
                    <div
                        style={{
                            fontSize: 13,
                            lineHeight: "1.55",
                            color: "#475569",
                            maxWidth: "850px",
                            fontWeight: 400,
                            letterSpacing: "-0.005em",
                            wordBreak: "break-word"
                        }}
                    >
                        {renderDescription(description)}
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        background: "linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.6) 100%)",
                        padding: "20px",
                        borderRadius: 12,
                        border: "1.5px solid rgba(203, 213, 225, 0.4)",
                        boxShadow: "inset 0 2px 6px rgba(0, 0, 0, 0.02)",
                        maxHeight: "220px",
                    }}
                >
                    {/* Product */}
                    <div>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#64748b",
                                marginBottom: 6,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Product
                        </div>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#0f172a",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                lineHeight: "1.4",
                            }}
                        >

                            {product}
                        </div>
                    </div>

                    {/* Source */}
                    <div>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#64748b",
                                marginBottom: 6,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Source
                        </div>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#0f172a",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                lineHeight: "1.4",
                            }}
                        >

                            {source || "N/A"}
                        </div>
                    </div>

                    {/* Owner */}
                    <div>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#64748b",
                                marginBottom: 6,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Owner
                        </div>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#0f172a",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                lineHeight: "1.4",
                            }}
                        >

                            {owner || "N/A"}
                        </div>
                    </div>

                    {/* Created Date */}
                    <div>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#64748b",
                                marginBottom: 6,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Created Date
                        </div>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#0f172a",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                lineHeight: "1.4",
                            }}
                        >

                            {createdDate}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeatureDetailsCard;
